import type { AgentProfile } from '~/core/entities/agentProfile/AgentProfile';

import { grammarInflection } from '~~/utils';

export interface AgentHighlightItem {
	value: string;
	label: string;
}

export type AgentHighlightSurface = 'hero' | 'performance';

const MONTHS_IN_YEAR = 12;

function formatCompactNumber(value: number): string {
	return value
		.toLocaleString('pt-BR', { maximumFractionDigits: 1 })
		.replace(/,0$/, '');
}

function abbreviatePrice(value: number): string {
	if (value >= 1_000_000) return `${formatCompactNumber(value / 1_000_000)}M`;
	if (value >= 1_000) return `${formatCompactNumber(value / 1_000)} mil`;

	return formatCompactNumber(value);
}

/**
 * Price range label, e.g. "R$ 2M – 15M".
 *
 * Collapses to a single value when both ends match, so an agent with one
 * property does not read as a range of itself.
 */
export function formatAgentPriceRange(
	priceRange: AgentProfile['portfolio']['priceRange']
): string | null {
	if (!priceRange) return null;

	const { min, max } = priceRange;
	if (!min && !max) return null;

	const from = abbreviatePrice(min || max);
	const to = abbreviatePrice(max || min);

	return from === to ? `R$ ${from}` : `R$ ${from} – ${to}`;
}

export function formatAgentTransactions(
	transactions: AgentProfile['transactions'] | null | undefined
): string | null {
	const total =
		(transactions?.sell.count ?? 0) + (transactions?.rent.count ?? 0);

	return total > 0 ? total.toLocaleString('pt-BR') : null;
}

/**
 * How long the agent has been with Pilar, derived from `createdAt`.
 *
 * The project has no date library, so the elapsed months are counted from the
 * calendar fields. Anything under a month still reads as "1 mês" — "0 meses"
 * would look like a bug to the visitor.
 *
 * @param createdAt - ISO date the agent joined
 * @param now - Reference date; injectable so the output is testable
 */
export function formatAgentTimeAtPilar(
	createdAt: string | null | undefined,
	now: Date = new Date()
): string | null {
	if (!createdAt) return null;

	const joinedAt = new Date(createdAt);
	if (Number.isNaN(joinedAt.getTime()) || joinedAt > now) return null;

	// UTC getters on both sides: the ISO date is parsed as UTC, so local getters
	// would shift the month for anyone west of Greenwich.
	const monthsElapsed =
		(now.getUTCFullYear() - joinedAt.getUTCFullYear()) * MONTHS_IN_YEAR +
		(now.getUTCMonth() - joinedAt.getUTCMonth()) -
		(now.getUTCDate() < joinedAt.getUTCDate() ? 1 : 0);

	if (monthsElapsed < MONTHS_IN_YEAR) {
		const months = Math.max(monthsElapsed, 1);
		return `${months} ${grammarInflection(months, 'mês', 'meses')}`;
	}

	const years = Math.floor(monthsElapsed / MONTHS_IN_YEAR);
	return `${years} ${grammarInflection(years, 'ano', 'anos')}`;
}

/**
 * Builds the highlight cards from the profile.
 *
 * Price range, transactions and time at Pilar are derived from the canonical
 * fields so they cannot drift from them. Response time arrives pre-formatted
 * because the contract has no raw equivalent.
 *
 * Cards without data are dropped rather than shown empty, following the rule
 * that unavailable information stays hidden on this page.
 *
 * @param profile - The profile to read the counters from
 * @param now - Reference date for the time at Pilar; injectable so it is testable
 * @param surface - Which surface is rendering; it sets the order of the cards
 */
export function buildAgentHighlights(
	profile: AgentProfile | null,
	now?: Date,
	surface: AgentHighlightSurface = 'performance'
): AgentHighlightItem[] {
	if (!profile) return [];

	const transactions = formatAgentTransactions(profile.transactions);
	const priceRange = formatAgentPriceRange(profile.portfolio.priceRange);
	const responseTime = profile.responseTime || null;
	const timeAtPilar = formatAgentTimeAtPilar(profile.createdAt, now);

	const candidates: Array<[string | null, string]> =
		surface === 'hero'
			? [
					[priceRange, 'Faixa de preço'],
					[responseTime, 'Tempo de resposta'],
					[transactions, 'Captações'],
					[timeAtPilar, 'Na Pilar']
				]
			: [
					[transactions, 'Captações'],
					[priceRange, 'Faixa de preço'],
					[responseTime, 'Tempo de resposta'],
					[timeAtPilar, 'Na Pilar']
				];

	return candidates
		.filter((entry): entry is [string, string] => entry[0] !== null)
		.map(([value, label]) => ({ value, label }));
}
